/**
 * McpServer factory — shared between stdio (index.ts) and HTTP (http.ts) entry points.
 */

import { McpServer } from "@modelcontextprotocol/sdk/server/mcp.js";

import { registerEntriesTools } from "./tools/entries.js";
import { registerRelationsTools } from "./tools/relations.js";
import { registerContextTools } from "./tools/context.js";
import { registerCollectionsTools } from "./tools/collections.js";
import { registerOrientTool } from "./tools/orient.js";
import { registerSmartCaptureTools } from "./tools/smart-capture.js";
import { registerWorkflowTools } from "./tools/workflows.js";
import { registerQualityTools } from "./tools/quality.js";
import { registerSessionTools } from "./tools/session.js";
import { registerGitChainTools } from "./tools/gitchain.js";
import { registerMapTools } from "./tools/maps.js";
import { registerWorkspaceTools } from "./tools/workspace.js";
import { registerResources } from "./resources/index.js";
import { registerPrompts } from "./prompts/index.js";
import { initToolSurface } from "./tool-surface.js";

export const SERVER_VERSION = "0.7.2";

const INSTRUCTIONS = [
  "Product Brain — the single source of truth for product knowledge.",
  "Terminology, standards, and core data all live here — no need to check external docs.",
  "",
  "## About Product Brain (PB)",
  "",
  "PB is a knowledge management system used by many product teams.",
  "These describe how PB works — not your product.",
  "",
  "### How PB Organizes Its Tools",
  "- **Tools** are for actions with side-effects or dynamic computation (capture, accept, search).",
  "- **Resources** are for stable, read-only data (orientation, terminology, collection schemas).",
  "- **Prompts** are for multi-step choreography (workflows, guided capture, deep dives).",
  "- Every tool, resource, and prompt works for ANY workspace — no bespoke logic.",
  "- Resource templates (URI params) provide workspace-specific data through generic patterns.",
  "- Tool count stays minimal: compound tools with `action` enums over many single-purpose tools.",
  "",
  "### How PB Handles Your Data",
  "- **Draft-first**: all writes create drafts. Nothing reaches SSOT without explicit user confirmation.",
  "- **Empty-workspace safe**: every tool and resource handles zero entries, zero collections, and fresh workspaces.",
  "- **Advisory, not blocking**: quality scores, contradiction checks, and coaching are informational. They never prevent an operation.",
  "- **Workspace-agnostic**: PB is a product used by many teams. No workspace-specific logic in server code.",
  "- **Self-documenting**: orient and server instructions teach agents how PB works. Cursor rules supplement but don't replace.",
  "",
  "### Tool Rename Notice (WP-484 S3, one release)",
  "The 37-tool surface was consolidated to 10 default compound tools (2 more behind PB_MODULES=gitchain).",
  "Old standalone names are gone — no alias window (ASM-43: zero external consumers). Map: " +
    "`start_pb`→`orient action=start`, `record_activation`→`orient action=record-activation`, " +
    "`session-wrapup`→`session action=wrapup-review|wrapup-commit`, `facilitate`→`session action=resume|commit-constellation`, " +
    "`update-entry`→`entries action=update`, `commit-entry`→`entries action=commit`, `get-history`→`entries action=history`, " +
    "`move-entry`→`entries action=move`, `verify-entry`→`entries action=verify`, `graph`→`relations action=find|suggest`, " +
    "`documents`→`context action=last-verified-brief`, `labels`→`collections action=label-*`, " +
    "`verify`→`quality action=verify-chain`, `audit`→`quality action=audit`, `health`→`workspace action=check|whoami|status|self-test`, " +
    "`get-usage-summary`→`workspace action=usage`, `governance-proposals`→`workspace action=proposals-*`, " +
    "`skills`→`workflows action=load-skill`, `chain-version`→`chain action=version.*`, `chain-branch`→`chain-review action=branch.*`, " +
    "`create-audience-map-set`/`map-slot`/`map-version`/`map-suggest`→`map action=create-audience-set|slot.*|version.*|suggest`.",
  "",
  "## Your Workspace Principles",
  "",
  "When you orient or start, PB surfaces active entries from your `principles`, `standards`,",
  "and `business-rules` collections. These are YOUR team's guardrails — respect them during implementation.",
  "If no principles exist yet, PB will tell you and suggest how to add them.",
  "",
  "The three governance collections:",
  "- **Principles** (`principles`) — beliefs that guide decisions. 'We believe X.' You reason from a principle.",
  "- **Standards** (`standards`) — conventions about how work is done. 'We do X this way.' You lint for a standard.",
  "- **Business Rules** (`business-rules`) — system constraints. 'The system must do X.' You test for a rule.",
  "",
  "## Workflow",
  "",
  "  1. Start: call `session action=start` to open a tracked write session, then `orient action=start` — it detects your workspace stage and returns the right guidance. Fresh workspaces get the pb-setup skill body; active workspaces get a standup briefing. (`orient` orients the session; only `session action=start` creates one.)",
  "  2. Re-orient: call `orient action=task` mid-session for task-scoped context or a compact status refresh.",
  "  3. Discover: use `entries action=search` to find entries, or `entries action=list` to browse.",
  "  4. Drill in: use `entries action=get entryId=\"...\"` for full details — data, labels, relations, history.",
  "  5. Context: use `context action=gather` with an entryId or a task description.",
  "  6. Capture: use `capture` to create entries — auto-links and scores in one call.",
  "  7. Accept: use `entries action=commit` to promote drafts to SSOT — only when the user confirms.",
  "  8. Connect: use `relations action=suggest` then `relations action=create` to build the graph.",
  "  9. Wrapup: call `session action=wrapup-review` before closing — reviews drafts, coaches quality, suggests links.",
  "  10. Close: call `session action=close` when done — records session activity. Auto-nudges if wrapup was skipped.",
  "",
  "Write tools (capture, entries action=update, relations, entries action=commit) require:",
  "  - An active agent session — call `session action=start` first, then `orient action=start` for context. Call `orient action=task` mid-session for a refresh.",
  "  - A readwrite API key scope",
  "",
  "Accept-on-confirm: always capture as draft first and show the user what was captured.",
  "Only call `entries action=commit` when the user explicitly confirms (e.g. 'accept', 'looks good', 'yes').",
  "This builds trust — the Chain (main) is SSOT; nothing goes there without user consent.",
  "",
  "Alignment-first: before proposing or building anything, check orient's Workspace Governance",
  "and Active bets sections. These are constraints, not reference material.",
  "If the proposal conflicts with a principle, standard, or business rule — stop, flag the conflict,",
  "and get explicit user confirmation before proceeding. Do not design around governance.",
  "If the work is outside the scope of active bets — stop, name the gap, and ask the user",
  "to confirm before designing. Do not propose implementation for out-of-scope work without a go-ahead.",
  "",
  "Workspace setup: use `collections action=create` and `collections action=update` to shape the workspace",
  "structure with the user. Ask what they need to track; presets are starting points, not fixed.",
  "",
  "Personalization: if you have context about the user from memory (prior work, recent",
  "conversations, team context), use it to personalize recommendations. For example,",
  "'Based on your recent pitch reviews, the gap most likely to matter is X.'",
  "The orient/start output gives you the workspace state; your memory fills in the human context.",
].join("\n");

export function createProductBrainServer(): McpServer {
  const server = new McpServer(
    { name: "Product Brain", version: SERVER_VERSION },
    { capabilities: { logging: {} }, instructions: INSTRUCTIONS },
  );

  initToolSurface();

  // WP-484 S3 (DEC-1466): default flipped from "core,gitchain" to "core" —
  // the derived ≤10-tool budget (spec §1) is only satisfiable with gitchain
  // opt-in. Pass PB_MODULES=core,gitchain to also register `chain` /
  // `chain-review` / `map` (13 tools total, still under every derived
  // budget except stack B — spec §3).
  const enabledModules = new Set(
    (process.env.PB_MODULES ?? "core")
      .split(",").map((m) => m.trim().toLowerCase()),
  );

  // Core surface — always registered. Exactly 10 tools (spec §3): the CI
  // count gate (__tests__/tool-count-gate.test.ts) pins default `tools/list` ≤ 10.
  registerSessionTools(server);
  registerEntriesTools(server);
  registerRelationsTools(server);
  registerContextTools(server);
  registerCollectionsTools(server);
  registerOrientTool(server);
  registerSmartCaptureTools(server);
  registerQualityTools(server);
  registerWorkflowTools(server);
  registerWorkspaceTools(server);

  // Module-gated — opt-in via PB_MODULES=core,gitchain (3 more tools: chain,
  // chain-review, map). Off the public default (DEC-1466).
  if (enabledModules.has("gitchain")) registerGitChainTools(server);
  if (enabledModules.has("gitchain")) registerMapTools(server);

  registerResources(server);
  registerPrompts(server);

  // 10 tools visible by default (13 with PB_MODULES=core,gitchain — WP-484
  // S3, TEN-2157). Write tools are guarded at handler level
  // (requireWriteAccess checks session + orient + key scope). Hiding tools
  // via disable() broke Claude's lazy tool indexing — it never re-indexed
  // after list_changed notifications.

  return server;
}
